/**
 * P2 지도·환경 페이지 (DESIGN §8.2 · RUNBOOK P6).
 *
 * 이 페이지가 지켜야 할 세 가지:
 *
 * 1. **지금 무엇을 보고 있는지 문장으로 적는다.** 실측 52만기에서 전국 뷰는 포인트
 *    상한(5만)을 상시 넘어 H3 로 강등된다 (§15.3). 강등이 예외가 아니라 기본 경로이므로
 *    "포인트를 보고 있다"고 착각하게 두면 안 된다.
 * 2. **색 구간의 경계를 인쇄한다.** 분위 구간은 경계를 숨기면 거짓말이 된다 (`lib/bins`).
 * 3. **꺼진 레이어를 숨기지 않는다** (§8.2.1 규칙 3).
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { SidePanel } from '@/components/SidePanel'
import { quantileBins } from '@/lib/bins'
import { useClientConfig } from '@/lib/client'
import { int, pct } from '@/lib/fmt'
import { useFilters } from '@/stores/filters'
import { LayerPanel } from './map/LayerPanel'
import { MapCanvas, type BasemapMode } from './map/MapCanvas'
import { SidoFlank } from './map/SidoFlank'
import { SidoPanel } from './map/SidoPanel'
import {
  KOREA,
  METRICS,
  useGeoData,
  ZOOM_POINTS,
  type MapMetric,
  type ViewState,
} from './map/useGeoData'
import './MapPage.css'

// 국토는 남북이 동서보다 길다 — 실측 위도폭 33.1~38.6°(약 610km) vs
// 경도폭 126~129.6°(약 320km). 가로가 긴 캔버스는 좌우에 바다만 채운다.
const CANVAS_H = 760
const CANVAS_W = 560
const TABLE_LIMIT = 300

/** 뷰포트 → bbox 문자열. 포인트는 전국을 받으면 상한을 넘어 서버가 강등한다. */
function bboxOf(view: ViewState): string | undefined {
  if (view.zoom < ZOOM_POINTS) return undefined
  // 대략적인 화면 폭/높이의 경위도 범위. 서버 필터가 목적이므로 정밀도는 이 정도면 된다
  const spanLng = 360 / 2 ** view.zoom
  const spanLat = spanLng * (CANVAS_H / CANVAS_W) * 0.75
  return [
    view.longitude - spanLng,
    view.latitude - spanLat,
    view.longitude + spanLng,
    view.latitude + spanLat,
  ].join(',')
}

/** 배경 지도 3단. '없음'은 타일 요청을 아예 끊는다 (§8.2.2) */
const BASEMAPS: { key: BasemapMode; label: string; hint: string }[] = [
  { key: 'full', label: '지도', hint: '원본 색 그대로 — 지형·도로가 가장 잘 보입니다' },
  { key: 'muted', label: '연한 무채색', hint: '채도를 빼 데이터 색이 앞에 서게 합니다' },
  { key: 'none', label: '없음', hint: '외부 타일 서버에 요청하지 않습니다' },
]

function fmtValue(metric: MapMetric, v: number | null): string {
  if (v == null) return '—'
  return metric === 'n_chargers' ? int(v) : pct(v, metric === 'availability' ? 2 : 1)
}

/** 정렬된 배열의 백분위. p 는 0~1 */
function quantile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0
  const i = Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * p)))
  return sorted[i] ?? 0
}

/**
 * 그려진 셀을 담는 뷰를 계산한다 (DESIGN §8.6.9 — 시도 중심으로 줌).
 *
 * **min/max 를 쓰면 안 된다.** 좌표 오류 55기가 섞여 있다 — (33.0, 125.0)
 * 센티널 54기가 서해 한복판에 뭉쳐 있고 경기 연천에 1기가 있다. min/max 로
 * 잡으면 어느 시도를 골라도 bbox 가 서해까지 늘어난다. 2~98 백분위로 자른다.
 *
 * 값을 config 에 박지 않고 **응답 좌표에서 계산**한다 — 하드코딩 금지.
 */
function fitTo(cells: { lat: number; lng: number }[]): ViewState | null {
  if (cells.length < 2) return null
  const lats = cells.map((c) => c.lat).sort((a, b) => a - b)
  const lngs = cells.map((c) => c.lng).sort((a, b) => a - b)
  const lat1 = quantile(lats, 0.02)
  const lat9 = quantile(lats, 0.98)
  const lng1 = quantile(lngs, 0.02)
  const lng9 = quantile(lngs, 0.98)
  const spanLng = Math.max(lng9 - lng1, 0.05)
  const spanLat = Math.max(lat9 - lat1, 0.05)
  // 가로·세로 중 더 빡빡한 쪽이 줌을 정한다. 0.8 은 가장자리 여유
  const zx = Math.log2((360 / spanLng) * 0.8)
  const zy = Math.log2((180 / spanLat) * (CANVAS_H / CANVAS_W) * 0.8)
  const zoom = Math.max(5.5, Math.min(11.5, Math.min(zx, zy)))
  return {
    longitude: (lng1 + lng9) / 2,
    latitude: (lat1 + lat9) / 2,
    zoom,
    pitch: 0,
    bearing: 0,
  }
}

export function MapPage() {
  const [view, setView] = useState<ViewState>(KOREA)
  const [metric, setMetric] = useState<MapMetric>('coverage')
  // 배경 표현. 'none' 이면 타일 요청이 아예 나가지 않는다 (§8.2.2)
  const [basemap, setBasemap] = useState<BasemapMode>('muted')
  const { data: config } = useClientConfig()

  const bbox = useMemo(() => bboxOf(view), [view])
  const geo = useGeoData(view, metric, bbox)

  /**
   * 시도 선택이 **바뀐 순간에만** 그 시도로 화면을 맞춘다.
   * 매 렌더마다 맞추면 사용자가 지도를 끌 수 없다 — 선택 키를 기억해 한 번만 움직인다.
   */
  const zcodeKey = useFilters((s) => s.zcode.join(','))
  const fittedFor = useRef<string | null>(null)
  useEffect(() => {
    if (fittedFor.current === zcodeKey) return
    if (geo.cells.length === 0) return // 아직 안 왔으면 다음 렌더에 다시 본다
    fittedFor.current = zcodeKey
    const next = zcodeKey === '' ? KOREA : fitTo(geo.cells)
    if (next) setView(next)
  }, [zcodeKey, geo.cells])

  /**
   * **지표 셋의 구간을 한 곳에서 낸다.**
   * 지도 범례와 좌우 시·도 막대가 같은 구간을 써야 "이 시도의 커버리지가
   * 전국 어디쯤인가"를 색으로 바로 읽을 수 있다. 따로 계산하면 같은 화면에서
   * 두 색 규칙이 생겨 서로를 반박한다.
   * 기준 모집단은 **지도에 그려진 셀**이다 — 범례에 인쇄된 경계가 그것이다.
   */
  const binsBy = useMemo(
    () => ({
      coverage: quantileBins(geo.cells.map((c) => c.coverage)),
      availability: quantileBins(geo.cells.map((c) => c.availability)),
      utilization: quantileBins(geo.cells.map((c) => c.utilization)),
      n_chargers: quantileBins(geo.cells.map((c) => c.n_chargers)),
    }),
    [geo.cells],
  )
  // 지도 색은 선택된 지표의 구간이다 — 위 표와 같은 객체라 어긋날 수 없다
  const bins = binsBy[metric]

  const metricMeta = METRICS.find((m) => m.key === metric)
  const sorted = useMemo(
    () => [...geo.cells].sort((a, b) => b.n_chargers - a.n_chargers).slice(0, TABLE_LIMIT),
    [geo.cells],
  )

  const table = (
    <div className="ctable">
      <table className="data-table">
        <caption className="sr-only">지도에 그려진 셀·충전기 값</caption>
        <thead>
          <tr>
            <th scope="col">{geo.isPoints ? '충전기' : 'H3 셀'}</th>
            <th scope="col" className="ta-r">
              충전기
            </th>
            <th scope="col" className="ta-r">
              커버리지
            </th>
            <th scope="col" className="ta-r">
              가동률
            </th>
            <th scope="col" className="ta-r">
              색 구간
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((c) => {
            const i = bins.indexOf(c.value)
            return (
              <tr key={c.id}>
                <td className="mono">{c.id}</td>
                <td className="num ta-r">{int(c.n_chargers)}</td>
                <td className="num ta-r">{pct(c.coverage, 1)}</td>
                <td className="num ta-r">{pct(c.availability)}</td>
                <td className="num ta-r">{i == null ? '값 없음' : `${i + 1}/${bins.colors.length}`}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  return (
    <div className="mappage">
      <header className="mappage__intro">
        <p className="mappage__lead">
          전국 <strong>52만기</strong>는 포인트 상한(5만)을 넘으므로 넓은 뷰에서는 자동으로
          H3 육각 집계로 <strong>강등</strong>됩니다. 강등은 예외가 아니라 기본 경로이며, 지금
          무엇을 보고 있는지는 아래에 항상 적혀 있습니다.
        </p>
        {config && config.basemap_style_url === '' && (
          <p className="mappage__nobase">
            베이스맵 없음 — <strong>외부 타일 서버에 요청하지 않습니다.</strong> 뷰포트가 곧
            "어느 지역을 조사하는가"이므로 기본값으로 두지 않았습니다 (§8.2.2).
          </p>
        )}
        {config && config.basemap_style_url !== '' && (
          <p className="mappage__nobase">
            {basemap === 'none' ? (
              <>
                배경 지도 <strong>없음</strong> — 이 상태에서는 외부 타일 서버에 요청하지
                않습니다. 위 버튼으로 켤 수 있습니다 (§8.2.2).
              </>
            ) : (
              <>
                배경 지도 켜짐 — <strong>지도를 볼 때마다 뷰포트가 타일 서버에 전달됩니다.</strong>{' '}
                「없음」을 고르면 요청이 끊깁니다 (§8.2.2). 지도 데이터 © OpenStreetMap 기여자.
              </>
            )}
          </p>
        )}
      </header>

      <div className="mappage__grid">
        <ChartFrame
          title="충전기 지도"
          question={`색 = ${metricMeta?.label} · ${metricMeta?.hint}`}
          meta={geo.meta}
          controls={
          <div className="mappage__ctl">
            <div className="mappage__metrics" role="group" aria-label="색 지표">
              {METRICS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={`mappage__metric${metric === m.key ? ' is-on' : ''}`}
                  aria-pressed={metric === m.key}
                  onClick={() => setMetric(m.key)}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {config && config.basemap_style_url !== '' && (
              <div className="mappage__metrics" role="group" aria-label="배경 지도">
                {BASEMAPS.map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    className={`mappage__metric${basemap === b.key ? ' is-on' : ''}`}
                    aria-pressed={basemap === b.key}
                    onClick={() => setBasemap(b.key)}
                    title={b.hint}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          }
          isLoading={geo.isLoading}
          error={geo.error}
          isEmpty={!geo.isLoading && geo.cells.length === 0}
          onRetry={geo.refetch}
          legend={bins.colors.map((color, i) => ({
            color,
            pattern: 'solid' as const,
            label:
              i === 0
                ? `≤ ${fmtValue(metric, bins.edges[0] ?? null)}`
                : i === bins.colors.length - 1
                  ? `> ${fmtValue(metric, bins.edges[i - 1] ?? null)}`
                  : `${fmtValue(metric, bins.edges[i - 1] ?? null)} ~ ${fmtValue(metric, bins.edges[i] ?? null)}`,
          }))}
          reading={`${
            geo.isPoints ? '충전기 한 대가 점 하나' : 'H3 육각형 한 칸이 그 안의 충전기 묶음'
          }이고, 색이 ${metricMeta?.label} 입니다. 지금은 ${geo.representation} 표현으로 ${int(
            geo.cells.length,
          )}개를 그렸습니다.`}
          encoding={[
            {
              role: '가로·세로',
              what: '실제 위치 (경도 · 위도)',
              detail:
                '베이스맵을 불러오지 않으므로 배경은 비어 있고, 국토 모양은 충전기가 있는 칸들이 만들어 냅니다.',
            },
            {
              role: '색',
              what: `${metricMeta?.label} · 분위 ${bins.colors.length}구간`,
              detail:
                '연할수록 낮고 진할수록 높습니다. 구간은 같은 개수씩 나눈 분위이며 경계값을 위 범례에 인쇄했습니다 — 경계를 숨긴 분위 지도가 거짓말입니다.',
            },
            {
              role: '회색 칸',
              what: '값을 계산할 수 없는 칸',
              detail:
                '0 과 같은 색을 주면 "관측 안 됨"이 "값이 0"으로 읽힙니다. 그래서 색 계열에서 빼고 회색으로 둡니다.',
            },
            {
              role: '칸 크기',
              what: `표현 단위 (${geo.representation})`,
              detail:
                'zoom 8 미만은 r7(넓은 육각), 8~11 은 r9(좁은 육각), 11 이상은 개별 포인트입니다. 값이 아니라 축척을 나타냅니다.',
            },
          ]}
          table={table}
          notes={[
            {
              label: '강등은 예외가 아니라 기본 경로다.',
              text: `현재 표현은 ${geo.representation} 이고 ${int(geo.cells.length)}개를 그렸습니다 (zoom ${view.zoom.toFixed(1)}). 전국 52만기는 포인트 상한 5만을 상시 넘습니다.`,
            },
            {
              label: '분위 구간의 경계를 인쇄했다.',
              text: '분위는 면적 대비 값을 왜곡합니다. 경계값을 범례에 적어 두어야 읽는 사람이 그 왜곡을 되짚을 수 있습니다.',
            },
            bins.nMissing > 0
              ? {
                  label: `값 없는 칸 ${int(bins.nMissing)}개.`,
                  text: '색 계열에서 빼고 회색으로 두었습니다. 지우지 않는 이유는 "그 위치에 충전기는 있는데 상태를 모른다"가 정보이기 때문입니다.',
                }
              : '',
            {
              label: '클러스터링을 쓰지 않는다.',
              text: '마커를 뭉치면 그 안의 상태 분포가 사라집니다 (§8.2).',
            },
            {
              label: '높이·반경으로 값을 표현하지 않는다.',
              text: '3D 원기둥은 시점에 따라 서로를 가려 비교가 불가능해집니다 (§8.2.1 규칙 1).',
            },
          ]}
        >
          {/* 지도를 세로로 세우면서 남은 좌우 공간에 시·도 현황을 붙인다
              (§8.6.9). 지도를 보면서 동시에 "어디가 큰가"를 읽는다. */}
          <div className="mappage__stage">
            <SidoFlank side="left" binsBy={binsBy} cells={geo.cells} />
            <MapCanvas
              cells={geo.cells}
              bins={bins}
              isPoints={geo.isPoints}
              view={view}
              onViewChange={setView}
              height={CANVAS_H}
              basemapUrl={config?.basemap_style_url || undefined}
              basemapMode={basemap}
            />
            <SidoFlank
              side="right"
              binsBy={binsBy}
              cells={geo.cells}
              nMixed={geo.nMixedZcode}
            />
          </div>
        </ChartFrame>
      </div>

      {/* 우 패널은 셸이 갖는다 (DESIGN §8.6.8) — 지도·분석만 쓰는 슬롯 */}
      <SidePanel>
        <SidoPanel />
        <LayerPanel config={config} representation={geo.representation} />
      </SidePanel>
    </div>
  )
}
